import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import { useConfirmDialog } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { clearAuthSession, ensureAuthenticated } from '../services/authGuardService';
import { getApiErrorMessage, readJsonSafe } from '../services/apiResponseService';
import TokenUsageModal from './TokenUsageModal';
import styles from '../styles/modules/MyJobOffers.module.css';
import headerStyles from '../styles/modules/Header.module.css';

interface JobRequest {
    _id: string;
    userId: string;
    jobRole: string;
    date?: string;
    startDate?: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
    place: string;
    payForTransport: boolean;
    status: string;
    description?: string;
    payRate?: string;
    facilityName?: string;
    numberOfStaff?: number;
    createdAt: string;
    updatedAt: string;
}

const MyJobOffers: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { confirmDelete } = useConfirmDialog();
    const { showToast } = useToast();
    const [jobOffers, setJobOffers] = useState<JobRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
    const [editingOfferId, setEditingOfferId] = useState<string | null>(null);
    const [editFormData, setEditFormData] = useState<Partial<JobRequest>>({});
    const [showCreateJobTokenModal, setShowCreateJobTokenModal] = useState(false);

    useEffect(() => {
        fetchMyJobOffers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchMyJobOffers = async () => {
        const auth = ensureAuthenticated();

        if (!auth.allowed) {
            showToast({ message: t('jobOffers.mustBeLoggedInToView'), type: 'error' });
            navigate(auth.redirectTo);
            return;
        }

        const token = auth.token;
        setLoading(true);
        setError('');

        try {
            const response = await fetch(getApiUrl('job-requests/my'), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    clearAuthSession();
                    navigate('/signin');
                    return;
                }
                throw new Error(`Failed to fetch job offers: ${response.statusText}`);
            }

            const data = await response.json();
            setJobOffers(data);
            console.log('Job offers:', data);
        } catch (error) {
            console.error('Error fetching job offers:', error);
            setError(error instanceof Error ? error.message : 'Failed to fetch job offers');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteOffer = async (offerId: string) => {
        const confirmed = await confirmDelete({
            title: t('common.confirm'),
            message: t('messages.deleteConfirm'),
            confirmLabel: t('common.delete'),
            cancelLabel: t('common.cancel')
        });
        if (!confirmed) {
            return;
        }

        const auth = ensureAuthenticated();
        if (!auth.allowed) {
            showToast({ message: t('jobOffers.mustBeLoggedIn') || 'You must be logged in', type: 'error' });
            navigate(auth.redirectTo);
            return;
        }

        const token = auth.token;

        try {
            const response = await fetch(getApiUrl(`job-requests/${offerId}`), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await readJsonSafe<any>(response);
                throw new Error(getApiErrorMessage(response, t('jobOffers.deleteFailed') || 'Failed to delete job offer', errorData));
            }

            // Remove from state
            setJobOffers(prev => prev.filter(offer => offer._id !== offerId));
            showToast({ message: t('jobOffers.deleteSuccess'), type: 'success' });
        } catch (error) {
            console.error('Error deleting job offer:', error);
            showToast({ message: error instanceof Error ? error.message : t('jobOffers.deleteFailed'), type: 'error' });
        }
    };

    const handleEditClick = (offer: JobRequest) => {
        setEditingOfferId(offer._id);
        setEditFormData({
            jobRole: offer.jobRole,
            date: offer.date,
            startTime: offer.startTime,
            endTime: offer.endTime,
            place: offer.place,
            payForTransport: offer.payForTransport,
            description: offer.description || '',
            payRate: offer.payRate || '',
            facilityName: offer.facilityName || ''
        });
    };

    const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        
        setEditFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleCancelEdit = () => {
        setEditingOfferId(null);
        setEditFormData({});
    };

    const handleUpdateOffer = async (offerId: string) => {
        const auth = ensureAuthenticated();
        if (!auth.allowed) {
            showToast({ message: t('jobOffers.mustBeLoggedIn') || 'You must be logged in', type: 'error' });
            navigate(auth.redirectTo);
            return;
        }

        const token = auth.token;

        try {
            const response = await fetch(getApiUrl(`job-requests/${offerId}`), {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(editFormData)
            });

            if (!response.ok) {
                const errorData = await readJsonSafe<any>(response);
                throw new Error(getApiErrorMessage(response, t('jobOffers.updateFailed') || 'Failed to update job offer', errorData));
            }

            const updatedOffer = await response.json();
            
            // Update in state
            setJobOffers(prev => prev.map(offer => 
                offer._id === offerId ? updatedOffer : offer
            ));
            
            setEditingOfferId(null);
            setEditFormData({});
            showToast({ message: t('jobOffers.updateSuccess'), type: 'success' });
        } catch (error) {
            console.error('Error updating job offer:', error);
            showToast({ message: error instanceof Error ? error.message : t('jobOffers.updateFailed'), type: 'error' });
        }
    };

    const handleStatusChange = async (offerId: string, newStatus: string) => {
        const auth = ensureAuthenticated();
        if (!auth.allowed) {
            showToast({ message: t('jobOffers.mustBeLoggedIn') || 'You must be logged in', type: 'error' });
            navigate(auth.redirectTo);
            return;
        }

        const token = auth.token;

        try {
            const response = await fetch(getApiUrl(`job-requests/${offerId}/status`), {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (!response.ok) {
                const errorData = await readJsonSafe<any>(response);
                throw new Error(getApiErrorMessage(response, t('jobOffers.statusUpdateFailed') || 'Failed to update status', errorData));
            }

            const updatedOffer = await response.json();
            
            // Update in state
            setJobOffers(prev => prev.map(offer => 
                offer._id === offerId ? updatedOffer : offer
            ));
            
            showToast({ message: t('jobOffers.statusUpdateSuccess'), type: 'success' });
        } catch (error) {
            console.error('Error updating status:', error);
            showToast({ message: error instanceof Error ? error.message : t('jobOffers.statusUpdateFailed'), type: 'error' });
        }
    };

    const toggleCardExpanded = (id: string) => {
        setExpandedCards(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const handleBackClick = () => {
        navigate('/dashboard');
    };

    const handleCreateOfferClick = () => {
        const auth = ensureAuthenticated();

        if (!auth.allowed) {
            showToast({ message: t('common.loginRequired') || 'Please log in to continue', type: 'error' });
            navigate(auth.redirectTo);
            return;
        }

        setShowCreateJobTokenModal(true);
    };

    const handleCreateOfferTokenUsed = () => {
        setShowCreateJobTokenModal(false);
        navigate('/create-job-offer');
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        const locale = t('common.locale') || 'en-US';
        return date.toLocaleDateString(locale, { 
            weekday: 'short',
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    };

    const formatTime = (time: string): string => {
        const [hours, minutes] = time.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${hour12}:${minutes} ${ampm}`;
    };

    const formatCreatedDate = (dateString: string): string => {
        const date = new Date(dateString);
        const locale = t('common.locale') || 'en-US';
        return date.toLocaleDateString(locale, { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
        });
    };

    return (
        <div className={styles.myJobOffersContainer}>
            <div className={styles.listPageHeader}>
                <button onClick={handleBackClick} className={headerStyles.backButton}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                </button>
                <h1 className={styles.headerTitle}>{t('jobOffers.title')}</h1>
                <button onClick={handleCreateOfferClick} className={styles.actionButton}>
                    {t('jobOffers.createNew')}
                </button>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    {error}
                </div>
            )}

            <div className={styles.offersSection}>
                {loading ? (
                    <div className={styles.loadingSpinner}>{t('common.loading')}</div>
                ) : jobOffers.length === 0 ? (
                    <div className={styles.noOffers}>
                        <div className={styles.noOffersIcon}>📋</div>
                        <h2>{t('jobOffers.noOffers')}</h2>
                        <p>{t('jobOffers.noOffersMessage')}</p>
                        <button 
                            onClick={handleCreateOfferClick} 
                            className={styles.createFirstButton}
                        >
                            {t('jobOffers.createFirst')}
                        </button>
                    </div>
                ) : (
                    <div className={styles.offersGrid}>
                        {jobOffers.map((offer) => {
                            const isExpanded = expandedCards.has(offer._id);
                            const isEditing = editingOfferId === offer._id;
                            
                            return (
                                <div key={offer._id} className={styles.offerCard}>
                                    {isEditing ? (
                                        /* Edit Form */
                                        <div className={styles.editForm}>
                                            <h3 className={styles.editTitle}>{t('jobOffers.editTitle')}</h3>
                                            
                                            <div className={styles.formGroup}>
                                                <label>{t('jobOffers.jobRole')}</label>
                                                <input
                                                    type="text"
                                                    name="jobRole"
                                                    value={editFormData.jobRole || ''}
                                                    onChange={handleEditInputChange}
                                                    className={styles.formInput}
                                                />
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>{t('jobOffers.facilityName')}</label>
                                                <input
                                                    type="text"
                                                    name="facilityName"
                                                    value={editFormData.facilityName || ''}
                                                    onChange={handleEditInputChange}
                                                    className={styles.formInput}
                                                    placeholder="e.g., Restaurant Name"
                                                />
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>{t('jobOffers.date')}</label>
                                                <input
                                                    type="date"
                                                    name="date"
                                                    value={editFormData.date || ''}
                                                    onChange={handleEditInputChange}
                                                    className={styles.formInput}
                                                />
                                            </div>

                                            <div className={styles.formRow}>
                                                <div className={styles.formGroup}>
                                                    <label>{t('jobOffers.startTime')}</label>
                                                    <input
                                                        type="time"
                                                        name="startTime"
                                                        value={editFormData.startTime || ''}
                                                        onChange={handleEditInputChange}
                                                        className={styles.formInput}
                                                    />
                                                </div>
                                                <div className={styles.formGroup}>
                                                    <label>{t('jobOffers.endTime')}</label>
                                                    <input
                                                        type="time"
                                                        name="endTime"
                                                        value={editFormData.endTime || ''}
                                                        onChange={handleEditInputChange}
                                                        className={styles.formInput}
                                                    />
                                                </div>
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>{t('jobOffers.location')}</label>
                                                <input
                                                    type="text"
                                                    name="place"
                                                    value={editFormData.place || ''}
                                                    onChange={handleEditInputChange}
                                                    className={styles.formInput}
                                                />
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>{t('jobOffers.payRate')}</label>
                                                <input
                                                    type="text"
                                                    name="payRate"
                                                    value={editFormData.payRate || ''}
                                                    onChange={handleEditInputChange}
                                                    className={styles.formInput}
                                                    placeholder="e.g., $15/hour or $150 total"
                                                />
                                            </div>

                                            <div className={styles.formGroup + ' ' + styles.checkboxGroup}>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        name="payForTransport"
                                                        checked={editFormData.payForTransport || false}
                                                        onChange={handleEditInputChange}
                                                    />
                                                    <span>{t('jobOffers.payForTransport')}</span>
                                                </label>
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>{t('jobOffers.description')}</label>
                                                <textarea
                                                    name="description"
                                                    value={editFormData.description || ''}
                                                    onChange={handleEditInputChange}
                                                    className={styles.formTextarea}
                                                    rows={4}
                                                    placeholder="Additional details about the job..."
                                                />
                                            </div>

                                            <div className={styles.editActions}>
                                                <button 
                                                    onClick={() => handleUpdateOffer(offer._id)}
                                                    className={styles.saveEditButton}
                                                >
                                                    {t('jobOffers.saveChanges')}
                                                </button>
                                                <button 
                                                    onClick={handleCancelEdit}
                                                    className={styles.cancelEditButton}
                                                >
                                                    {t('jobOffers.cancel')}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className={styles.cardHeader}>
                                                <div className={styles.offerMainInfo}>
                                                    <h3 className={styles.offerRole}>
                                                        {t(`roles.${offer.jobRole.toLowerCase().replace(/\s+/g, '').replace('/', '').replace('&', '')}`)}
                                                        {offer.numberOfStaff && offer.numberOfStaff > 1 && (
                                                            <span className={styles.staffCount}> ({offer.numberOfStaff})</span>
                                                        )}
                                                    </h3>
                                                </div>
                                                <div className={styles.statusSelector}>
                                                    <select 
                                                        value={offer.status}
                                                        onChange={(e) => handleStatusChange(offer._id, e.target.value)}
                                                        className={`${styles.statusDropdown} ${styles[`status${offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}`]}`}
                                                    >
                                                        <option value="open">{t('jobOffers.statusValues.open')}</option>
                                                        <option value="filled">{t('jobOffers.statusValues.filled')}</option>
                                                        <option value="cancelled">{t('jobOffers.statusValues.cancelled')}</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className={styles.cardBody}>
                                        <div className={styles.offerInfo}>
                                            {offer.facilityName && (
                                                <div className={styles.infoRow + ' ' + styles.facilityRow}>
                                                    <span className={styles.infoIcon}>🏢</span>
                                                    <span className={styles.infoText + ' ' + styles.facilityName}>{offer.facilityName}</span>
                                                </div>
                                            )}
                                            {offer.date ? (
                                                <>
                                                    <div className={styles.infoRow}>
                                                        <span className={styles.infoIcon}>📅</span>
                                                        <span className={styles.infoText}>{formatDate(offer.date)}</span>
                                                    </div>
                                                    {offer.startTime && (
                                                        <div className={styles.infoRow}>
                                                            <span className={styles.infoIcon}>🕒</span>
                                                            <span className={styles.infoText}>
                                                                {formatTime(offer.startTime)}
                                                            </span>
                                                        </div>
                                                    )}
                                                </>
                                            ) : offer.startDate && offer.endDate ? (
                                                <div className={styles.infoRow}>
                                                    <span className={styles.infoIcon}>📅</span>
                                                    <span className={styles.infoText}>
                                                        {formatDate(offer.startDate)} - {formatDate(offer.endDate)}
                                                    </span>
                                                </div>
                                            ) : null}
                                            <div className={styles.infoRow}>
                                                <span className={styles.infoIcon}>📍</span>
                                                <span className={styles.infoText}>{offer.place}</span>
                                            </div>
                                            {offer.payRate && (
                                                <div className={styles.infoRow + ' ' + styles.payRate}>
                                                    <span className={styles.infoIcon}>💰</span>
                                                    <span className={styles.infoText}>{offer.payRate}</span>
                                                </div>
                                            )}
                                            {offer.payForTransport && (
                                                <div className={styles.infoRow + ' ' + styles.transportProvided}>
                                                    <span className={styles.infoIcon}>🚗</span>
                                                    <span className={styles.infoText}>{t('jobOffers.transportIncluded')}</span>
                                                </div>
                                            )}
                                        </div>

                                        {offer.description && (
                                            <div className={styles.descriptionSection}>
                                                <button 
                                                    className={styles.toggleDescriptionBtn}
                                                    onClick={() => toggleCardExpanded(offer._id)}
                                                >
                                                    {isExpanded ? '▼' : '▶'} {t('jobOffers.description')}
                                                </button>
                                                
                                                {isExpanded && (
                                                    <div className={styles.descriptionContent}>
                                                        {offer.description}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className={styles.offerMeta}>
                                            <span className={styles.createdDate}>
                                                {t('jobOffers.postedOn')} {formatCreatedDate(offer.createdAt)}
                                            </span>
                                        </div>
                                    </div>

                                            <div className={styles.cardFooter}>
                                                <button 
                                                    onClick={() => handleEditClick(offer)}
                                                    className={styles.editButton}
                                                >
                                                    {t('jobOffers.edit')}
                                                </button>
                                                <button 
                                                    onClick={() => handleDeleteOffer(offer._id)}
                                                    className={styles.deleteButton}
                                                >
                                                    {t('jobOffers.delete')}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <TokenUsageModal
                type="jobPost"
                isOpen={showCreateJobTokenModal}
                onClose={() => setShowCreateJobTokenModal(false)}
                onTokenUsed={handleCreateOfferTokenUsed}
            />
        </div>
    );
};

export default MyJobOffers;
